import axios, { AxiosResponse, AxiosError } from 'axios'
import { useAuthStore } from '@/stores/auth'
import { useLoadingStore } from '@/stores/loading'
import { useNotificationStore } from '@/stores/notification'
import { useCacheStore } from '@/stores/cache'
import { trackApiCall, trackCacheHit } from '../metrics'
import { csrfManager } from '../csrfManager'
import { networkService } from '../../network'
import { API_CONFIG } from '../config'

/**
 * Determina qual página de login usar baseado na rota atual
 */
function getLoginRoute(): string {
  const currentPath = window.location.pathname
  if (currentPath.startsWith('/usuario')) {
    return '/usuario/login'
  }
  return '/login'
}

function isPasswordRecoveryPath(pathname?: string): boolean {
  const currentPath = (pathname || window.location.pathname || '').toLowerCase()
  return currentPath.includes('/forgot-password') || currentPath.includes('/reset-password/')
}

/**
 * Verifica se a requisição é de login/autenticação
 */
function isAuthRequest(url?: string): boolean {
  if (!url) return false
  return (
    url.includes('/login') ||
    url.includes('/auth/login') ||
    url.includes('/register') ||
    url.includes('/auth/register')
  )
}

/**
 * Verifica se a requisição é para uma rota web (Sanctum) vs API
 */
function isWebRoute(url?: string, baseURL?: string): boolean {
  if (!url) return false
  if (baseURL && !baseURL.includes('/api')) {
    return true
  }
  const fullUrl = baseURL ? `${baseURL}${url}` : url
  return !fullUrl.includes('/api/')
}

/**
 * Obtém o cookie CSRF do Sanctum
 */
async function getCsrfCookie(): Promise<boolean> {
  try {
    const sanctumBaseUrl =
      (import.meta as any).env?.VITE_SANCTUM_BASE_URL ||
      ((import.meta as any).env?.VITE_API_BASE_URL || 'https://bloopbrasil.com.br/api').replace(/\/api\/?$/, '') ||
      'https://bloopbrasil.com.br'

    await axios.get(`${sanctumBaseUrl}/sanctum/csrf-cookie`, {
      withCredentials: true,
    })
    return true
  } catch (error) {
    console.warn('Erro ao obter CSRF cookie:', error)
    return false
  }
}

// Interface para métricas de API
interface ApiMetrics {
  requestCount: number
  successCount: number
  errorCount: number
  averageResponseTime: number
  lastRequestTime: number
}

// Armazenar métricas em memória
const apiMetrics: ApiMetrics = {
  requestCount: 0,
  successCount: 0,
  errorCount: 0,
  averageResponseTime: 0,
  lastRequestTime: 0,
}

/**
 * Gera a chave de cache baseada na requisição
 */
function getCacheKey(response: AxiosResponse): string {
  return (
    response.config.metadata?.cacheKey ||
    `${response.config.method}:${response.config.url}:${JSON.stringify(response.config.params || '')}`
  )
}

export const setupResponseInterceptor = (axiosInstance: any) => {
  axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => {
      const loadingStore = useLoadingStore()
      const notificationStore = useNotificationStore()
      const cacheStore = useCacheStore()

      // Atualizar métricas
      updateMetrics(true, response.config.metadata?.startTime)

      // Rastrear chamada da API
      const responseTime = response.config.metadata?.startTime ? Date.now() - response.config.metadata.startTime : 0
      trackApiCall(response.config.url || '', true, responseTime)

      // === CACHE DE RESPOSTAS ===
      if (response.config.method === 'get' && response.config.metadata?.cache !== false) {
        const url = response.config.url || ''

        // Verificar se deve cachear
        if (API_CONFIG.shouldCache(url)) {
          const cacheKey = getCacheKey(response)
          const cacheTTL = response.config.metadata?.cacheTTL || API_CONFIG.getCacheTTL(url)

          cacheStore.set(cacheKey, response.data, cacheTTL)

          if (import.meta.env.DEV) {
            console.log('[Cache] Response cached:', {
              key: cacheKey,
              ttl: cacheTTL,
              url,
            })
          }
        }
      }

      // Parar loading
      if (!response.config.metadata?.background) {
        loadingStore.stopLoading(response.config.metadata?.key || 'global')
      }

      // Mostrar notificação de sucesso se configurado
      if (response.config.metadata?.showSuccess) {
        notificationStore.showSuccess(response.data.message || 'Operação realizada com sucesso')
      }

      return response
    },
    async (error: AxiosError) => {
      const authStore = useAuthStore()
      const loadingStore = useLoadingStore()
      const notificationStore = useNotificationStore()

      // === TRATAMENTO DE CACHE HIT (do request interceptor) ===
      if ((error as any)._isCacheHit || (error as any)._isOfflineCache) {
        // Este erro é na verdade um cache hit - retornar como sucesso
        const mockResponse = (error as any).response
        loadingStore.stopLoading('global')
        return mockResponse
      }

      // === TRATAMENTO DE OFFLINE ===
      if ((error as any)._isOffline || error.code === 'NETWORK_OFFLINE') {
        const loadingStore = useLoadingStore()
        loadingStore.stopLoading('global')

        notificationStore.showError('Sem conexão com a internet. Verifique sua rede.')

        if (import.meta.env.DEV) {
          console.warn('[Network] Offline detected - request rejected')
        }

        return Promise.reject(error)
      }

      // Atualizar métricas de erro
      updateMetrics(false, error.config?.metadata?.startTime)

      // Rastrear chamada da API com erro
      const responseTime = error.config?.metadata?.startTime ? Date.now() - error.config.metadata.startTime : 0
      trackApiCall(error.config?.url || '', false, responseTime)

      // Parar loading
      loadingStore.stopLoading('global')

      // === TRATAMENTO DE 401 (Unauthorized) ===
      if (error.response?.status === 401) {
        // Se for uma requisição de login/registro, não redirecionar
        const isAuth = isAuthRequest(error.config?.url)
        if (isAuth) {
          return Promise.reject(error)
        }

        // Verificar se é uma rota web (Sanctum) vs API
        const isWeb = isWebRoute(error.config?.url, error.config?.baseURL)

        if (isWeb) {
          // Para rotas web, 401 pode ser causado por falta de CSRF token
          if (!error.config?.metadata?.csrfRetry) {
            console.warn('[401 Web Route] Tentando obter CSRF cookie antes de logout...', {
              url: error.config?.url,
              baseURL: error.config?.baseURL,
            })

            try {
              // Invalidar e buscar novo CSRF
              csrfManager.invalidate()
              const csrfObtained = await csrfManager.getToken()

              if (csrfObtained) {
                // Tentar novamente a requisição com novo CSRF
                const retryConfig = {
                  ...error.config,
                  metadata: { ...error.config?.metadata, csrfRetry: true },
                }

                console.log('[401 Web Route] CSRF cookie obtido, tentando novamente...')
                return axiosInstance.request(retryConfig)
              }
            } catch (csrfError) {
              console.error('[401 Web Route] Erro ao obter CSRF cookie:', csrfError)
            }
          }

          console.warn('[401 Web Route] Erro 401 em rota web - pode ser CSRF ou sessão expirada', {
            url: error.config?.url,
            message: error.response?.data?.message,
          })

          return Promise.reject(error)
        }

        // Para rotas API, tentar renovar token se possível
        // Verifica se há um token atual (não precisa mais de refresh_token separado)
        const hasToken = localStorage.getItem('auth_token')

        if (hasToken && !error.config?.metadata?.retry) {
          try {
            await authStore.refreshAuthToken()

            // Retry da requisição original
            const retryConfig = {
              ...error.config,
              metadata: { ...error.config?.metadata, retry: true },
            }

            return axiosInstance.request(retryConfig)
          } catch (refreshError) {
            console.error('[401 API Route] Erro ao renovar token:', refreshError)
            authStore.logout()
            if (isPasswordRecoveryPath()) {
              console.warn('[DeepLinkDebug][401] evitando redirect para login em rota de recuperação', {
                currentPath: window.location.pathname,
                requestUrl: error.config?.url,
              })
            } else {
              window.location.href = getLoginRoute()
            }
            return Promise.reject(refreshError)
          }
        } else {
          // Sessão expirada ou sem token, redirecionar para login
          console.warn('[401 API Route] Sessão expirada ou sem token')
          authStore.logout()
          if (isPasswordRecoveryPath()) {
            console.warn('[DeepLinkDebug][401] mantendo usuário na recuperação de senha', {
              currentPath: window.location.pathname,
              requestUrl: error.config?.url,
            })
          } else {
            window.location.href = getLoginRoute()
          }
        }
      }

      // === TRATAMENTO DE 419 (CSRF Token Mismatch) ===
      if (error.response?.status === 419) {
        if (!error.config?.metadata?.csrfRetry) {
          console.warn('[419] CSRF token mismatch - obtendo novo token...')

          try {
            csrfManager.invalidate()
            const csrfObtained = await csrfManager.getToken()

            if (csrfObtained) {
              const retryConfig = {
                ...error.config,
                metadata: { ...error.config?.metadata, csrfRetry: true },
              }

              console.log('[419] Novo CSRF obtido, tentando novamente...')
              return axiosInstance.request(retryConfig)
            }
          } catch (csrfError) {
            console.error('[419] Erro ao obter novo CSRF:', csrfError)
          }
        }

        notificationStore.showError('Sua sessão expirou. Por favor, tente novamente.')
        return Promise.reject(error)
      }

      // === TRATAMENTO DE TIMEOUT ===
      if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
        const isSlowNetwork = networkService.isSlowConnection()

        if (import.meta.env.DEV) {
          console.warn('[Timeout] Request timeout:', {
            url: error.config?.url,
            timeout: error.config?.timeout,
            connectionType: networkService.getConnectionType(),
          })
        }

        notificationStore.showError(
          isSlowNetwork
            ? 'Conexão lenta detectada. Tente novamente.'
            : 'Tempo de solicitação excedido. Tente novamente.',
        )

        return Promise.reject(error)
      }

      // === TRATAMENTO DE ERRO DE REDE ===
      if (!error.response && error.message === 'Network Error') {
        notificationStore.showError('Erro de conexão. Verifique sua internet.')

        if (import.meta.env.DEV) {
          console.warn('[Network] Network Error:', {
            url: error.config?.url,
            online: networkService.isOnline(),
            connectionType: networkService.getConnectionType(),
          })
        }

        return Promise.reject(error)
      }

      // === TRATAMENTO DE OUTROS ERROS ===
      const errorMessage = getErrorMessage(error)

      // Mostrar notificação de erro se não for request de background
      if (!error.config?.metadata?.background) {
        notificationStore.showError(errorMessage)
      }

      return Promise.reject(error)
    }
  )
}

function getErrorMessage(error: AxiosError): string {
  if (error.response?.data?.message) {
    return error.response.data.message
  }

  if (error.response?.data?.error) {
    return error.response.data.error
  }

  if (error.response?.data?.errors) {
    const errors = error.response.data.errors
    if (typeof errors === 'object') {
      return Object.values(errors).flat().join(', ')
    }
  }

  switch (error.response?.status) {
    case 400:
      return 'Dados inválidos'
    case 403:
      return 'Acesso negado'
    case 404:
      return 'Recurso não encontrado'
    case 422:
      return 'Dados de validação inválidos'
    case 429:
      return 'Muitas tentativas. Aguarde um momento.'
    case 500:
      return 'Erro interno do servidor'
    case 502:
    case 503:
    case 504:
      return 'Servidor temporariamente indisponível'
    default:
      return 'Erro de conexão'
  }
}

// Função para atualizar métricas
function updateMetrics(success: boolean, startTime?: number) {
  apiMetrics.requestCount++
  apiMetrics.lastRequestTime = Date.now()

  if (success) {
    apiMetrics.successCount++
  } else {
    apiMetrics.errorCount++
  }

  if (startTime) {
    const responseTime = Date.now() - startTime
    apiMetrics.averageResponseTime = (apiMetrics.averageResponseTime + responseTime) / 2
  }
}

// Função para obter métricas
export function getApiMetrics(): ApiMetrics {
  return { ...apiMetrics }
}

// Função para resetar métricas
export function resetApiMetrics(): void {
  apiMetrics.requestCount = 0
  apiMetrics.successCount = 0
  apiMetrics.errorCount = 0
  apiMetrics.averageResponseTime = 0
  apiMetrics.lastRequestTime = 0
}
